/* This example requires Tailwind CSS v2.0+ */
import CartContext from '@/components/CartContext';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { Fragment, useContext, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  IndexLabel,
  IsDoFollowLabel,
  NewsLabel,
  SponsorLabel2,
  TurnaroundTimeLabel,
} from '../labels';
import StatusLabel from '../statusLabel';
import Tooltip from '../tooltip';

export default function PublicationDetailsModal({
  setIsOpen,
  isOpen,
  publication,
  handlePublicationInquiryOpen,
  canViewPricing,
  canViewDoFollowAndSponsored,
  showCTA = true,
}) {
  const cancelButtonRef = useRef(null);
  const { handleAddItem } = useContext(CartContext);

  const handleAddToCart = () => {
    handleAddItem(publication);
    setIsOpen(false);
  };

  const haveExampleScreenshot =
    publication?.deal || publication?.story
      ? haveExampleScreenshot
      : publication?.exampleScreenshot;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[999]" onClose={setIsOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary opacity-20 transition-opacity" />
        </Transition.Child>
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end sm:items-center justify-center min-h-full sm:p-4 xl:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative bg-white rounded-xl text-left overflow-y-auto overflow-x-hidden shadow-xl transform transition-all max-w-[390px] sm:max-w-[390px] md:max-w-[490px] xl:max-w-[1512px] sm:max-h-[800px] xl:max-h-[926px]">
                {/* className={`flex flex-col xl:flex-row-reverse gap-8 justify-between px-[20px] xl:px-[56px] pt-[20px] xl:pt-[56px] ${
                    haveExampleScreenshot
                      ? 'pb-16'
                      : 'pb-[20px] xl:pb-[56px] max-w-[680px]'
                  }` */}

                {haveExampleScreenshot && (
                  <button
                    type="button"
                    className="button buy large w-[80%] lg:w-[350px] shadow-sm sticky  top-[44rem] max-w-[350px] mx-auto z-10 sm:flex xl:hidden"
                    onClick={handleAddToCart}
                  >
                    <PlusIcon className="block h-4 w-4" aria-hidden="true" />

                    <span className="ml-2">Accept and apply</span>
                  </button>
                )}

                <div
                  className={classNames(
                    'px-5 xl:px-14 pt-5 xl:pt-14 flex flex-col gap-8 -mt-12',
                    haveExampleScreenshot
                      ? 'pb-16 xl:grid xl:grid-cols-2'
                      : 'pb-5 xl:pb-14 max-w-[680px]'
                  )}
                >
                  {/* PUBLICATION & STORY DETAILS */}
                  <div className="flex flex-col  xl:col-start-2">
                    <div className="flex flex-row justify-end">
                      <button onClick={() => setIsOpen(false)}>
                        <span className="text-gray-500">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-6 h-6"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </span>
                      </button>
                    </div>
                    <h2 className="text-[32px] xl:text-[44px] leading-[32px] xl:leading-[52px]">
                      {publication?.story
                        ? publication?.story?.title
                        : publication?.name}
                    </h2>
                    {publication?.story && (
                      <div>
                        <p>{publication?.story?.description}</p>
                        <p className="mt-4">
                          <span className="font-bold">Requirements:</span>
                        </p>
                        <p>{publication?.story?.requirements}</p>
                      </div>
                    )}

                    {publication?.isExclusive && (
                      <Tooltip
                        content="We are the only authorized resellers for this outlet"
                        className="max-w"
                      >
                        <StatusLabel status={5} title="Exclusive" />
                      </Tooltip>
                    )}
                    <p className="my-4">
                      Please carefully review the restrictions and the example
                      article before applying.
                    </p>
                    <div
                      className={`flex flex-col bg-white rounded-xl sm:min-w-full ${
                        haveExampleScreenshot
                          ? 'xl:min-w-[568px]'
                          : 'xl:min-w-[568px]'
                      }`}
                    >
                      <div className="flex flex-col">
                        <div className="flex flex-col xs:flex-row justify-between gap-4 px-6 py-6">
                          <img
                            src={
                              publication?.logo
                                ? publication.logo?.attributes?.url ||
                                  publication.logo?.url
                                : 'https://via.placeholder.com/56'
                            }
                            className="rounded-full"
                            width={'56px'}
                            height={'56px'}
                          />
                          <div className="flex flex-col grow">
                            <p className="font-bold">{publication?.name}</p>
                            <a
                              className="text-gray-400 focus:outline-none"
                              href={publication?.websiteUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <p>{publication?.websiteUrl}</p>
                            </a>
                          </div>
                          <div className={classNames('font-bold')}>
                            {canViewPricing && (
                              <p>{publication?.getFormattedPrice()}</p>
                            )}
                          </div>
                        </div>
                        <hr className="border-1" />
                        <dl className="font-normal px-6 py-6">
                          <dt className="sr-only">Title</dt>
                          <dd className="text-gray-700 capitalize flex flex-col">
                            <div className="flex mb-[16px]">
                              <span className="bg-gray-100 rounded-full px-[8px] py-[2px] mr-1">
                                {publication?.category}
                              </span>
                            </div>
                            <TurnaroundTimeLabel
                              turnaroundTime={publication?.turnaroundTime}
                            />
                          </dd>
                          <dd></dd>
                          <dt className="sr-only ">Email</dt>
                          <dd className="mt-2 text-gray-500  flex gap-2 flex-wrap">
                            {canViewDoFollowAndSponsored && (
                              <>
                                <IsDoFollowLabel
                                  isDoFollow={publication?.isDoFollow}
                                  doFollowLinksAllowed={
                                    publication.doFollowLinksAllowed
                                  }
                                />
                              </>
                            )}
                            {/* {publication.disclaimer ? ( */}
                            {/* <DisclaimerLabel
                              disclaimer={publication.disclaimer}
                         */}

                            {publication?.isSponsored && (
                              <SponsorLabel2
                                isSponsored={publication?.isSponsored}
                              />
                            )}

                            <NewsLabel news={publication?.news} />

                            <IndexLabel indexed={publication?.isIndexed} />
                          </dd>
                        </dl>
                        {publication?.requirements && (
                          <>
                            <hr className="border-1" />
                            <div className="flex flex-row justify-between gap-4 px-6 py-6">
                              <div
                                className="p-4 text-sm text-yellow-800 rounded-lg bg-yellow-50 w-full border border-yellow-300 max-w-[520px]"
                                role="alert"
                              >
                                <span className="flex flex-row gap-3.5 items-start text-orange-700">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-5 h-5"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                                    />
                                  </svg>
                                  <div className="flex flex-col">
                                    <span className="font-bold">
                                      The publication selected has restrictions:
                                    </span>
                                    <ReactMarkdown className="text-red-600 prose prose-sm  overflow-y-scroll max-h-[200px]">
                                      {publication?.requirements}
                                    </ReactMarkdown>
                                  </div>
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                        {showCTA && (
                          <>
                            <hr className="border-1 hidden lg:flex" />
                            <div className="hidden xl:flex flex-row justify-between gap-4 px-6 py-6">
                              {publication.requiresInquiry ? (
                                <button
                                  className="button large w-full shadow-sm"
                                  onClick={() =>
                                    handlePublicationInquiryOpen(publication)
                                  }
                                >
                                  <span className="ml-2">Inquire</span>
                                </button>
                              ) : (
                                <button
                                  className="button large w-full shadow-sm"
                                  onClick={handleAddToCart}
                                >
                                  <PlusIcon
                                    className="block h-4 w-4"
                                    aria-hidden="true"
                                  />

                                  <span className="ml-2">Accept and apply</span>
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {haveExampleScreenshot && (
                    <div className="max-w-[776px] sm:h-auto xl:h-[926px] sm:max-h-full xl:max-h-[926px] sm:overflow-visible xl:overflow-auto pb-20  xl:col-start-1 xl:row-start-1">
                      <img
                        src={
                          haveExampleScreenshot?.attributes?.url ||
                          publication?.exampleScreenshot?.url
                        }
                      />
                    </div>
                  )}

                  <div className="flex xl:hidden flex-row justify-between gap-4 px-6 py-6">
                    {publication.requiresInquiry ? (
                      <button
                        className="button-secondary large w-full shadow-sm"
                        onClick={() =>
                          handlePublicationInquiryOpen(publication)
                        }
                      >
                        <span className="ml-2">Inquire</span>
                      </button>
                    ) : (
                      <>
                        {!haveExampleScreenshot && (
                          <button
                            className="button buy large w-full shadow-sm"
                            onClick={handleAddToCart}
                          >
                            <PlusIcon
                              className="block h-4 w-4"
                              aria-hidden="true"
                            />

                            <span className="ml-2">Accept and apply</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
